import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Auth, EmailAuthProvider, User as FireUser, IdTokenResult, reauthenticateWithCredential, signInWithCustomToken, user as firebaseUser$ } from '@angular/fire/auth';
import { collection, collectionData, doc, docData, Firestore, getDoc, query, Timestamp, where } from '@angular/fire/firestore';
import { Router } from '@angular/router';
import { HttpService, NetworkService } from '@damofire/data-angular-commons';
import { AUTH_PATHS, DivisionRole, IdbAuthStoreService, OrganizationLink, User } from '@damofire/data-security';
import { Division, DivisionGuardService, LinksClaims, OrganizationInfos, RolesClaims, UserClaims } from '@damofire/data-stas';
import { FORM_AUTO_COMPLETE_DEBOUNCE_TIME, hasAnyRole, hasRole } from '@damofire/uix-commons';
import { ParsedToken } from '@firebase/auth';
import { setUser } from '@sentry/angular';
import { SESSION_TOKENS_COLLECTION, USERS_COLLECTION, USERS_LINKS_COLLECTION, logger } from '@damofire/data-commons';
import { BehaviorSubject, merge, Observable, PartialObserver, Subject, Subscription } from 'rxjs';
import { debounceTime, filter, map } from 'rxjs/operators';
import { SubSink } from 'subsink';
import { environment } from '../../environments/environment';
import { RouterInterceptorService } from '../common/service/router-interceptor.service';

@Injectable()
export class AppAuthService extends DivisionGuardService {
  private readonly _userSub: Subject<User> = new BehaviorSubject<User>(null);
  private readonly userReady = new BehaviorSubject<boolean>(false);
  private readonly authSubSink = new SubSink();
  private sessionTokenSub: Subscription;

  private permissionsChangedSub = new SubSink();
  private _currentUser: User | null;
  private _roles: string[] = [];
  private _administrating: OrganizationLink[];
  private $administrating = new BehaviorSubject<OrganizationLink[]>(null);
  private _tokenIssuedAt: Date;
  // Warehouses
  private _managing: DivisionRole[] = [];
  private _representing: DivisionRole[] = [];
  private _canReadAndWrite: DivisionRole[] = [];
  private _canReadOnly: DivisionRole[] = [];
  private $canReadOnly = new BehaviorSubject<DivisionRole[]>(null);

  private _authenticationLock: Promise<void>;

  private loggingOut = false;

  firebaseUser$: BehaviorSubject<FireUser> = new BehaviorSubject(null);
  userSubscription: Subscription;

  private readonly http = inject(HttpService);
  private readonly router = inject(Router);
  private readonly networkService = inject(NetworkService);
  private readonly routerInterceptorService = inject(RouterInterceptorService);
  private readonly auth = inject(Auth);
  private readonly idbAuthStoreService = inject(IdbAuthStoreService);
  private readonly firestore = inject(Firestore);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    super();
    this.setAuthenticationLock();
    this.listenOnNetworkChange();
  }

  async isAuthenticated(): Promise<boolean> {
    await this.authenticationLock();
    return !!this.auth.currentUser;
  }

  isAuthenticated$(): Observable<boolean> {
    return this.firebaseUser$.pipe(map((firebaseUser) => !!firebaseUser));
  }

  personify(userId: string): Promise<void> {
    logger.info('Initiating personification', { category: 'auth', userId });
    return this.http.post('utils-personify', { userId }).then(
      async (loginResponse) => {
        logger.info('Custom token from personification received', { category: 'auth' });
        const auth = await signInWithCustomToken(this.auth, loginResponse.data);

        if (auth.user) {
          await this.finalizeUserLogin(auth.user);
          await this.router.navigate(['/dashboard']);
        }
      },
      (err) => logger.error('Cannot personify user', { category: 'auth', error: err }),
    );
  }

  async logout(): Promise<void> {
    this.loggingOut = true;
    this.userSubscription.unsubscribe();
    this.permissionsChangedSub?.unsubscribe();
    this.authSubSink?.unsubscribe();
    this.sessionTokenSub?.unsubscribe();

    this.userReady.next(false);
    this._userSub.next(null);
    this._currentUser = null;
    this._administrating = [];
    this._managing = [];
    this._representing = [];
    this._canReadAndWrite = [];
    this._canReadOnly = [];
    this.$canReadOnly.next([]);
    this.$administrating.next([]);

    this.routerInterceptorService.reset();
    await this.router.navigateByUrl('/logout');
  }

  reset(): void {
    this.loggingOut = false;
    this.setAuthenticationLock();
  }

  subscribe(observer: PartialObserver<User>): Subscription {
    return this._userSub.subscribe(observer);
  }

  async currentUser(): Promise<User> {
    await this.authenticationLock();
    return this._currentUser;
  }

  administrating(): OrganizationLink[] {
    return this._administrating ? this._administrating.slice() : [];
  }

  observeAdministrating(): Observable<OrganizationLink[]> {
    return this.$administrating.asObservable();
  }

  private managing(): DivisionRole[] {
    return this._managing ? this._managing.slice() : [];
  }

  representing(): DivisionRole[] {
    return this._representing ? this._representing?.slice() : [];
  }

  async canReadOrg(organizationId: string): Promise<boolean> {
    await this.authenticationLock();

    if (await this.hasRoles(['GSR_STAX_ADMIN', 'GSR_STAX_VIEWER'])) {
      return true;
    }

    if (this.administrating().some((element) => element._id === organizationId)) {
      return true;
    }

    logger.debug('User not a company administrator, evaluating division links', { category: 'auth' });
    const divisionRoles = this._managing.concat(this._canReadAndWrite, this._canReadOnly);

    const canRead = divisionRoles.some((element) => element.orgId === organizationId);
    logger.debug(`canReadOrg result: ${canRead}`, { category: 'auth' });
    return canRead;
  }

  async canReadDivision(divisionId: string): Promise<boolean> {
    await this.authenticationLock();

    if (await this.hasRoles(['GSR_STAX_ADMIN', 'GSR_STAX_VIEWER'])) {
      return true;
    }

    logger.debug('User not a user administrator, evaluating division links', { category: 'auth' });
    const divisionRoles = this._managing.concat(this._canReadAndWrite, this._canReadOnly);

    const canRead = divisionRoles.some((element) => element._id === divisionId);
    logger.debug(`canReadDivision result: ${canRead}`, { category: 'auth' });
    return canRead;
  }

  async roles(): Promise<string[]> {
    await this.authenticationLock();
    return this._roles;
  }

  async divisions(): Promise<DivisionRole[]> {
    await this.authenticationLock();
    return this._canReadOnly ? this._canReadOnly.slice() : []; // Contient la somme de managing / readWrite et readOnly
  }

  observeDivisions(): Observable<DivisionRole[]> {
    return this.$canReadOnly.asObservable();
  }

  async isRegularWatsonUser(): Promise<boolean> {
    const roles = await this.roles();
    // return false as soon as we find a role that is not GSR_WATSON_USER or GSR_STORAGE_USER
    return !roles.some((role) => !['GSR_WATSON_USER', 'GSR_STORAGE_USER'].includes(role));
  }

  hasRole(role: string): Promise<boolean> {
    return this.roles().then((r) => hasRole(r, role));
  }

  hasRoles(roles: string[]): Promise<boolean> {
    return this.roles().then((r) => hasAnyRole(r, roles));
  }

  async authenticationLock(): Promise<void> {
    await this.auth.authStateReady();
    return this._authenticationLock;
  }

  async canAddUser(organizationId: string, divisionId?: string): Promise<boolean> {
    await this.authenticationLock();

    if (await this.hasRole('GSR_STAX_ADMIN')) {
      return true;
    }

    logger.debug('User not user administrator, evaluating org admin links', { category: 'auth' });
    if (this.administrating().some((element) => element._id === organizationId)) {
      return true;
    }

    logger.debug('User not org admin, evaluating managing links', { category: 'auth' });
    if (this.hasDivisionRole(this.managing(), organizationId, divisionId)) {
      return true;
    }

    logger.debug('User not manager, evaluating rep links and role', { category: 'auth' });
    const internalRepRole = await this.hasRole('GSR_WATSON_REP');
    return internalRepRole && this.hasDivisionRole(this.representing(), organizationId, divisionId);
  }

  hasDivisionRole(divisionRoles: DivisionRole[], organizationId: string, divisionId?: string): boolean {
    if (divisionId) {
      return divisionRoles.some((element) => element._id === divisionId);
    } else {
      // If a divisionId was not provided, we check if we have at least one in the organization
      return divisionRoles.some((element) => element.orgId === organizationId);
    }
  }

  isDivisionRep(divisionId: string): boolean {
    return !!this._representing[divisionId];
  }

  async canEditDivision(organizationInfos: Division | OrganizationInfos): Promise<boolean> {
    await this.authenticationLock();

    if (await this.hasRole('GSR_STAX_ADMIN')) {
      return true;
    }

    const organizationalObject: OrganizationInfos =
      'division' in organizationInfos
        ? organizationInfos
        : {
            division: organizationInfos,
            organization: organizationInfos.organization,
          };

    if (this.administrating().some((element) => element._id === organizationalObject.organization?._id)) {
      return true;
    }

    logger.debug('User not administrator, evaluating read-write links', { category: 'auth' });
    const canEdit = this._canReadAndWrite.some((element) => element._id === organizationalObject.division._id);

    logger.debug(`canEditDivision result: ${canEdit}`, { category: 'auth' });
    return canEdit;
  }

  async canEditOrg(organizationId: string): Promise<boolean> {
    await this.authenticationLock();

    if (await this.hasRole('GSR_STAX_ADMIN')) {
      return true;
    }
    logger.debug('User not a company administrator, evaluating admin links', { category: 'auth' });
    const links: OrganizationLink[] = this.administrating();
    const isInAdminLinks = links.some((element) => {
      return element._id === organizationId;
    });

    logger.debug(`canEditOrg result: ${isInAdminLinks}`, { category: 'auth' });
    return isInAdminLinks;
  }

  private listenOnSessionToken(rolesClaims: RolesClaims, userId: string): Promise<LinksClaims> {
    this.sessionTokenSub?.unsubscribe();
    return new Promise((resolve) => {
      logger.info('Getting session token', { category: 'auth', userId });
      this.sessionTokenSub = docData(doc(this.firestore, `${SESSION_TOKENS_COLLECTION}/${userId}`))
        .pipe(debounceTime(FORM_AUTO_COMPLETE_DEBOUNCE_TIME))
        .subscribe({
          next: async (sessionDoc) => {
            if (sessionDoc) {
              logger.info('Got session token', { category: 'auth', userId });
              this.setClaims(rolesClaims, sessionDoc);
              resolve(sessionDoc);
            } else {
              logger.warn('Session token missing, renewing', { category: 'auth', userId });
              await this.renewSession(true);
            }
          },
          error: (e) => logger.error('Error listening on session token', { category: 'auth', userId, error: e }),
        });
    });
  }

  private async collectUserInformation(firebaseUser: FireUser): Promise<void> {
    logger.info('Collecting user information', { category: 'auth', uid: firebaseUser.uid });

    let rolesClaims: RolesClaims;
    let linksClaims: LinksClaims;
    let firebaseToken: IdTokenResult;
    let user: User;

    if (this.networkService.connected) {
      firebaseToken = await firebaseUser.getIdTokenResult();
      this._tokenIssuedAt = new Date(+firebaseToken.claims.iat * 1000);
      rolesClaims = firebaseToken.claims as unknown as RolesClaims;
      linksClaims = await this.listenOnSessionToken(rolesClaims, firebaseUser.uid);
      logger.info('Got firebase auth claims', { category: 'auth', uid: firebaseUser.uid });
    } else if (environment.offline) {
      let claims: UserClaims;
      [claims, user] = await this.generateOfflineClaims(firebaseUser);
      if (!claims) {
        this.router.navigateByUrl(AUTH_PATHS.SIGN_IN).catch(console.error);
        throw new Error(`Unable to find claims for ${firebaseUser.uid}`);
      } else {
        const { roles, ...linksFromOffline } = claims;
        rolesClaims = { roles };
        linksClaims = linksFromOffline;
      }
    } else {
      logger.error('Network offline, attempting token fetch anyway', { category: 'auth' });
      firebaseToken = await firebaseUser.getIdTokenResult();
      rolesClaims = firebaseToken as unknown as RolesClaims;
      logger.info('Firebase auth claim fallback', { category: 'auth', uid: firebaseUser.uid });
    }

    this.setClaims(rolesClaims, linksClaims);

    try {
      if (this.networkService.connected) {
        const currentUserRef = doc(this.firestore, `${USERS_COLLECTION}/${firebaseUser.uid}`);
        user = await getDoc(currentUserRef).then((snap) => ({ ...snap.data(), _id: snap.id }));
        if (user) {
          this._currentUser = user;
          this.listenOnUserPermissionsChange();
          this._userSub.next(user);
          logger.info('Got user details', { category: 'auth', userId: user._id });
          const mergedClaims: UserClaims = { ...rolesClaims, ...linksClaims };
          this.idbAuthStoreService.saveToken(firebaseUser.uid, mergedClaims, this._currentUser).catch(console.error);
        }
      } else {
        this._currentUser = user;
        this._userSub.next(user);
        logger.info('Got user details (offline)', { category: 'auth', userId: user._id });
      }
    } catch (e) {
      logger.error('collectUserInformation error', { category: 'auth', error: e });
      if (e.name?.startsWith('Firebase')) {
        await this.router.navigate(['/502']);
      }
    }
  }

  private setClaims(rolesClaim: RolesClaims, linksClaims?: LinksClaims): void {
    logger.info('Setting user claims', { category: 'auth' });

    if (linksClaims) {
      this._administrating = Object.values(linksClaims.administrator || {});
      this._managing = Object.values(linksClaims.manager || {});
      this._representing = Object.values(linksClaims.representative || {});
      this._canReadAndWrite = this._managing.concat(Object.values(linksClaims.collaborator || {})).concat(this._representing);
      this._canReadOnly = this._canReadAndWrite.concat(Object.values(linksClaims.observer || {}));

      this.$administrating.next(this._administrating);
      this.$canReadOnly.next(this._canReadOnly);
    }
    this._roles = rolesClaim.roles || [];
    this.userReady.next(true);
  }

  private async generateOfflineClaims(firebaseUser: FireUser): Promise<[UserClaims, User]> {
    const storedUser = await this.idbAuthStoreService.getToken(firebaseUser.uid);
    if (storedUser) {
      logger.info('Got offline claims', { category: 'auth', uid: storedUser.uid });
    }
    return [storedUser?.claims, storedUser?.user];
  }

  async renewSession(forceRefresh = false): Promise<void> {
    if (this.loggingOut) {
      logger.info('Skipping session renewal, user logging out', { category: 'auth' });
      return Promise.resolve(null);
    }

    try {
      logger.info('Renewing session', { category: 'auth' });
      const linksClaims = await this.http.post('utils-renewSession');
      const idToken = await this.auth.currentUser?.getIdTokenResult(forceRefresh);
      if (idToken) {
        const firebaseClaims = idToken.claims as ParsedToken & RolesClaims;
        this._tokenIssuedAt = new Date(+firebaseClaims.iat * 1000);
        this.setClaims(firebaseClaims, linksClaims);
      }
    } catch (err) {
      logger.error('Cannot renew session', { category: 'auth', error: err });
    }
  }

  async validatePassword(password: string): Promise<boolean> {
    const user = this.auth.currentUser;

    try {
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
      return true;
    } catch (err) {
      logger.warn('Reauthentication failed', { category: 'auth', error: err });
      return false;
    }
  }

  private setAuthenticationLock(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    this._authenticationLock = new Promise((resolve) => {
      this.userSubscription = firebaseUser$(this.auth).subscribe(async (u: FireUser | null) => {
        this.firebaseUser$.next(u);
        if (u && (!this._roles?.length || this.sessionTokenSub?.closed)) {
          await this.finalizeUserLogin(u);
          logger.info('Login completed', { category: 'auth', uid: u.uid });
          resolve();
        } else {
          resolve();
        }
      });
    });
  }

  private listenOnNetworkChange(): void {
    this.networkService.status.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((n) => {
      if (n) {
        logger.info('Network back online, renewing session', { category: 'network' });
        this.setAuthenticationLock();
      } else {
        this.sessionTokenSub?.unsubscribe();
        logger.warn('Network offline, stopped session listener', { category: 'network' });
      }
    });
  }

  private finalizeUserLogin(user: FireUser): Promise<void> {
    setUser({ id: user.uid });
    return this.collectUserInformation(user);
  }

  private listenOnUserPermissionsChange(): void {
    this.permissionsChangedSub.unsubscribe();
    const userId = this._currentUser._id;

    const links = collectionData(
      query(collection(this.firestore, `${USERS_COLLECTION}/${userId}/${USERS_LINKS_COLLECTION}`), where(`audit.lastModifiedDate`, '>', this._tokenIssuedAt)),
    ).pipe(
      filter((l) => l?.length > 0),
      map(() => 'links'),
    );

    const roles = docData<User>(doc(this.firestore, `${USERS_COLLECTION}/${userId}`)).pipe(
      filter((u) => (u?.audit.authModifiedDate as Timestamp)?.toMillis() >= this._tokenIssuedAt.getTime()),
      map(() => 'roles'),
    );

    this.permissionsChangedSub.add(
      merge(links, roles)
        .pipe(debounceTime(10000))
        .subscribe(async (type) => {
          logger.info('User permissions changed', { category: 'auth', type });
          await this.renewSession(true);
          // Reset listener for another change with a new timestamp
          this.listenOnUserPermissionsChange();
        }),
    );
  }

  onUserReady(): Observable<boolean> {
    return this.userReady;
  }
}
